<script setup lang="ts">
import { computed } from 'vue'
import type { IActivityByModulesWidget } from '@/modules/dashboard/types/IActivityByModulesWidget'
import { useChartColors } from '@/utils/chartColors'

interface Props {
  widgetData: IActivityByModulesWidget
  currentTarget?: string
  flat?: boolean
  loading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  flat: false,
  loading: false,
})

const { generateColors } = useChartColors()

const modules = computed(() => {
  const dataObj = props.widgetData.data
  if (!dataObj || !dataObj.labels || !dataObj.datasets || dataObj.datasets.length === 0) {
    return []
  }
  const labels = dataObj.labels
  const dataset = dataObj.datasets[0]
  const fallbackColors = generateColors(labels.length)
  const colors = dataset.colors?.length ? dataset.colors : fallbackColors

  const total = dataset.data.reduce((acc: number, val: number) => acc + (val || 0), 0)

  return labels.map((label, idx) => {
    const val = dataset.data[idx] || 0
    const pct = total > 0 ? Math.round((val / total) * 100) : 0
    return {
      name: label.name,
      percentage: pct,
      color: colors[idx] || fallbackColors[idx] || 'rgb(var(--v-theme-primary))',
    }
  })
})
</script>

<template>
  <VCard :variant="flat ? 'flat' : undefined" class="card-widget-modules" :class="[flat ? 'pa-0 bg-transparent' : 'pa-4']">
    <div class="d-flex align-center gap-2 mb-4">
      <div
        v-show="widgetData.show_icon && widgetData.icon"
        class="chart-icon-wrapper"
        :style="{ background: widgetData.icon_background }"
      >
        <VIcon :icon="widgetData.icon" size="sm" />
      </div>
      <span class="text-caption font-weight-medium text-high-emphasis">{{ widgetData.name }}</span>
    </div>

    <!-- Загрузка (скелетоны) -->
    <div v-if="loading" class="d-flex flex-column gap-3">
      <div v-for="i in 4" :key="i" class="d-flex flex-column mb-1">
        <div class="d-flex align-center justify-space-between mb-1">
          <VSkeletonLoader type="text" width="40%" class="bg-transparent my-0" />
          <VSkeletonLoader type="text" width="10%" class="bg-transparent my-0" />
        </div>
        <div class="progress-bar-track">
          <VSkeletonLoader type="text" class="bg-transparent progress-bar-skeleton-loader" />
        </div>
      </div>
    </div>

    <!-- Основной контент -->
    <div v-else class="d-flex flex-column gap-3">
      <div v-for="mod in modules" :key="mod.name" class="d-flex flex-column mb-1">
        <div class="d-flex align-center justify-space-between mb-1">
          <span class="text-caption font-weight-medium text-high-emphasis">{{ mod.name }}</span>
          <span class="text-caption font-weight-semibold text-medium-emphasis">{{ mod.percentage }}%</span>
        </div>
        <div class="progress-bar-track">
          <div
            class="progress-bar-fill"
            :style="{ width: `${mod.percentage}%`, backgroundColor: mod.color }"
          />
        </div>
      </div>
    </div>
  </VCard>
</template>

<style lang="scss" scoped>
.card-widget-modules {
  width: 100%;
}

.progress-bar-track {
  width: 100%;
  height: 6px;
  background-color: rgba(var(--v-theme-on-surface), 0.08);
  border-radius: 4px;
  overflow: hidden;
}

.progress-bar-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.3s ease;
}

.progress-bar-skeleton-loader {
  height: 6px !important;
  width: 100%;

  :deep(.v-skeleton-loader__text) {
    height: 6px !important;
    margin: 0 !important;
    border-radius: 4px !important;
  }
}

.gap-3 {
  gap: 12px;
}
</style>
